import { useValidationStore } from "@/zapi-frontend/stores/validation";
import { useCallback, useEffect, useMemo } from "react";
import { QuestionAnswer } from "@/zapi-frontend/shared/const/validation-questions";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProgressStore } from "@/zapi-frontend/stores/progress";
export function useValidations(projectId: string) {
  const {
    questions,
    isValidating,
    isFetchingQuestions,
    currentlyValidating,
    validatingChapterIds,
    validateAllQuestions,
    fetchQuestions,
    submitQuestionAnswer,
    setChapterCurrent
  } = useValidationStore();
  const { activeChapter, updateChapterCurrent, updateChapterCompleteness } = useChapterStore();
  const isCurrent = useMemo(() => activeChapter?.isCurrent ?? false, [activeChapter]);

  const { setProgress, setLoading, setQuestionsCount } = useProgressStore();

  useEffect(() => {
    setLoading(isFetchingQuestions || isValidating || !!currentlyValidating);
  }, [isFetchingQuestions, isValidating, currentlyValidating, setLoading]);

  useEffect(() => {
    if (!activeChapter) return;
    const validCount = questions.filter((q) => q.answer === "yes").length;
    const invalidCount = questions.filter((q) => q.answer === "no").length;
    const nullCount = questions.filter((q) => !q.answer).length;

    const totalCount = validCount + invalidCount + nullCount;
    const percentComplete = totalCount > 0 ? Math.round((validCount / totalCount) * 100) : 0;
    setProgress(percentComplete);
    setQuestionsCount({ total: totalCount, valid: validCount });
  }, [questions, activeChapter?.id, activeChapter, setProgress, setQuestionsCount]);

  useEffect(() => {
    if (!activeChapter?.id) return;
    fetchQuestions(projectId, activeChapter?.id);
  }, [fetchQuestions, projectId, activeChapter?.id]);

  const validateAll = useCallback(async () => {
    if (!activeChapter?.id) return;
    await validateAllQuestions(projectId, activeChapter?.id);
    updateChapterCurrent(activeChapter?.id.toString(), true);

    // Wait for the next tick to ensure state is updated
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Get the latest state from the store
    const currentQuestions = useValidationStore.getState().questions;
    const validCount = currentQuestions.filter((q) => q.answer === "yes").length;
    const invalidCount = currentQuestions.filter((q) => q.answer === "no").length;
    const nullCount = currentQuestions.filter((q) => !q.answer).length;

    const totalCount = validCount + invalidCount + nullCount;
    const percentComplete = totalCount > 0 ? Number((validCount / totalCount).toFixed(2)) : 0;

    updateChapterCompleteness({
      chapterId: activeChapter?.id.toString(),
      completeness: Number(percentComplete.toFixed(2)),
      valid: validCount,
      total: totalCount
    });
  }, [validateAllQuestions, projectId, activeChapter?.id, updateChapterCurrent, updateChapterCompleteness]);

  const submitQuestion = useCallback(
    async (slug: string, status: QuestionAnswer) => {
      if (!activeChapter?.id) return;
      await submitQuestionAnswer(projectId, activeChapter?.id, slug, status, undefined);

      // Wait for the next tick to ensure state is updated
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Get the latest state from the store
      const currentQuestions = useValidationStore.getState().questions;
      const validCount = currentQuestions.filter((q) => q.answer === "yes").length;
      const invalidCount = currentQuestions.filter((q) => q.answer === "no").length;
      const nullCount = currentQuestions.filter((q) => !q.answer).length;

      const totalCount = validCount + invalidCount + nullCount;
      const percentComplete = totalCount > 0 ? Number((validCount / totalCount).toFixed(2)) : 0;

      updateChapterCompleteness({
        chapterId: activeChapter?.id.toString(),
        completeness: percentComplete,
        valid: validCount,
        total: totalCount
      });
    },
    [submitQuestionAnswer, projectId, activeChapter?.id, updateChapterCompleteness]
  );

  const updateCurrent = useCallback(
    (current: boolean) => {
      if (!activeChapter?.id) return;
      updateChapterCurrent(activeChapter?.id.toString(), current);
      if (current) {
        setChapterCurrent(projectId, activeChapter?.id);
      }
    },
    [activeChapter?.id, updateChapterCurrent, projectId, setChapterCurrent]
  );

  return {
    questions,
    validateAll,
    isValidating,
    currentlyValidating,
    submitQuestion,
    isCurrent,
    isFetchingQuestions,
    updateCurrent,
    validatingChapterIds
  };
}
